import React from 'react';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { Checkbox } from '../../ui/checkbox';
import { Switch } from '../../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { FieldLabel, FieldHint, FieldError, errorProps } from './fields';

// #685: booking_type governs the assignment control directly below it — the
// hint states the effect at the point of choice instead of only relabelling
// a field further down. #790: a 1:1 link books exactly one coach.
const TYPE_HINTS = {
    one_on_one: 'Every booking goes to the coach selected below.',
    round_robin: 'Bookings rotate across the coaches below, in the order set in Admin → Coaches.',
};

// #753: why a row cannot be added (or will change on save). Neutral on
// purpose for paused/inactive coaches — in this release they are still booked
// while assigned; excluding them is #755.
const ROW_FLAGS = {
    paused: { badge: 'Paused', hint: 'Paused in Admin → Coaches' },
    inactive: { badge: 'Inactive', hint: 'Deactivated in Admin → Coaches' },
    suspended: { badge: 'Suspended user', hint: 'Account suspended in Admin → Organization' },
    not_coach: { badge: 'Not a coach', hint: 'Add a coach profile in Admin → Coaches, or untick to remove' },
    removed: { badge: 'Removed user', hint: 'No longer in this organization; removed when you save' },
};

// A coach's flag follows its status (a coach whose user left the org is
// 'removed', #790 audit); any other row's follows its kind.
const flagKey = (row) => (row.kind === 'coach' ? row.status : row.kind);
const rowFlag = (row) => ROW_FLAGS[flagKey(row)];

const initials = (name = '') => name.split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join('') || '?';

// What the assignment control says before (or instead of) any rows.
const MEMBERS_STATUS_TEXT = {
    loading: 'Loading coaches…',
    failed: "Couldn't load coaches. Close and reopen the editor to try again.",
    ready: 'No coaches yet — add them in Admin → Coaches.',
};

// #790: the 1:1 hint for a flagged current coach where the checklist's wording
// does not fit a dropdown: there is nothing to untick, and a link whose only
// coach left the org cannot be saved until another is picked.
const ONE_ON_ONE_HINTS = {
    not_coach: 'Add a coach profile in Admin → Coaches, or pick another coach',
    removed: 'No longer in this organization — pick another coach',
};

/**
 * #790: a 1:1 link books exactly one coach (the server refuses more), so it
 * gets a single dropdown instead of the round-robin checklist. Items are every
 * coach who can be newly assigned plus the current coach in whatever state,
 * with the checklist's badge, so a paused, inactive, suspended, non-coach or
 * removed coach can still be seen and replaced (#753). The trigger is the
 * focusable control, so it carries `id="bl-assigned-users"` for validation.
 */
function OneOnOneCoachSelect({ value, members, membersStatus, error, onPick }) {
    const items = members.filter(row => row.addable || row.user_id === value);
    const current = members.find(row => row.user_id === value);
    const flag = current && rowFlag(current);
    const hint = flag && (ONE_ON_ONE_HINTS[flagKey(current)] || flag.hint);
    const describedBy = [error && 'bl-assigned-users-error', hint && 'bl-assigned-coach-hint'].filter(Boolean).join(' ');
    let statusText = '';
    if (membersStatus !== 'ready' || members.length === 0) statusText = MEMBERS_STATUS_TEXT[membersStatus];
    else if (items.length === 0) statusText = 'No coach can be assigned right now — add or resume one in Admin → Coaches.';
    return (
        <>
            <Select value={value} onValueChange={onPick}>
                <SelectTrigger id="bl-assigned-users" data-testid="bl-assigned-coach" disabled={membersStatus !== 'ready'}
                    aria-invalid={error ? true : undefined} aria-describedby={describedBy || undefined}>
                    <SelectValue placeholder="Select a coach" />
                </SelectTrigger>
                <SelectContent>
                    {items.map(row => (
                        <SelectItem key={row.user_id} value={row.user_id}>
                            {[row.display_name, row.title, rowFlag(row)?.badge]
                                .filter((part, i, parts) => part && parts.indexOf(part) === i).join(' · ')}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
            {hint && <p id="bl-assigned-coach-hint" className="text-xs text-amber-700 mt-1" data-testid="bl-assigned-coach-hint">{hint}</p>}
            {statusText && <p className="text-xs text-stone-500 mt-1" data-testid="bl-assigned-users-empty">{statusText}</p>}
        </>
    );
}

/**
 * Step 1 — what the link is, how long a meeting is, and who takes it.
 *
 * `members` are the assignment rows from `buildMemberOptions` (#753): coach
 * profiles from Admin → Coaches, then any assigned user who is not one.
 * #790: `onTypeChange` and `onPickCoach` live in BookingLinkModal, which keeps
 * a 1:1 link at one coach and sets `assigneeNotice` when it drops any.
 */
export default function DetailsSection({
    form, setForm, members = [], membersStatus = 'ready', errorFor, onTypeChange, onPickCoach, assigneeNotice = '',
}) {
    const toggleUser = (userId) => {
        const current = form.assigned_users;
        const exists = current.find(u => u.user_id === userId);
        if (exists) {
            setForm({ ...form, assigned_users: current.filter(u => u.user_id !== userId) });
        } else {
            setForm({ ...form, assigned_users: [...current, { user_id: userId, weight: 1 }] });
        }
    };

    const updateWeight = (userId, weight) => {
        setForm({
            ...form,
            assigned_users: form.assigned_users.map(u =>
                u.user_id === userId ? { ...u, weight: Math.max(0, parseInt(weight) || 0) } : u
            ),
        });
    };

    const isRoundRobin = form.booking_type === 'round_robin';
    const nameError = errorFor('bl-name');
    const assigneesError = errorFor('bl-assigned-users');

    return (
        <div className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                    <FieldLabel htmlFor="bl-name">Name</FieldLabel>
                    <Input id="bl-name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="30 Min Meeting" className="mt-1.5" data-testid="bl-name" {...errorProps('bl-name', nameError)} />
                    <FieldError id="bl-name" error={nameError} />
                </div>
                <div>
                    <FieldLabel htmlFor="bl-slug">URL Slug</FieldLabel>
                    <Input id="bl-slug" value={form.slug} onChange={e => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })} placeholder="30min-meeting" className="mt-1.5" data-testid="bl-slug" />
                </div>
            </div>
            <div>
                <FieldLabel htmlFor="bl-description">Description</FieldLabel>
                <Textarea id="bl-description" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="A quick 30 min catch up..." rows={3} className="mt-1.5" data-testid="bl-description" />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                    <FieldLabel htmlFor="bl-duration">Duration (min)</FieldLabel>
                    <Select value={String(form.duration)} onValueChange={v => setForm({ ...form, duration: parseInt(v) })}>
                        <SelectTrigger id="bl-duration" className="mt-1.5" data-testid="bl-duration"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            {[15, 30, 45, 60, 90, 120].map(d => <SelectItem key={d} value={String(d)}>{d} min</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
                <div>
                    <FieldLabel htmlFor="bl-buffer">Buffer (min)</FieldLabel>
                    <Select value={String(form.buffer_time)} onValueChange={v => setForm({ ...form, buffer_time: parseInt(v) })}>
                        <SelectTrigger id="bl-buffer" className="mt-1.5" data-testid="bl-buffer"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            {[0, 5, 10, 15, 30].map(b => <SelectItem key={b} value={String(b)}>{b === 0 ? 'None' : `${b} min`}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
                <div>
                    <FieldLabel htmlFor="bl-type">Type</FieldLabel>
                    <Select value={form.booking_type} onValueChange={onTypeChange}>
                        <SelectTrigger id="bl-type" className="mt-1.5" data-testid="bl-type" aria-describedby="bl-type-hint"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="one_on_one">1:1 Meeting</SelectItem>
                            <SelectItem value="round_robin">Round Robin</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </div>
            <FieldHint id="bl-type-hint" data-testid="bl-type-hint">{TYPE_HINTS[form.booking_type] || TYPE_HINTS.one_on_one}</FieldHint>
            {!isRoundRobin && assigneeNotice && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5" role="status" data-testid="bl-assignee-notice">
                    {assigneeNotice}
                </p>
            )}

            {/* Assigned coaches. Round robin: a checklist where each coach also
                gets a number of turns (stored as `weight`, #754); #753: rows come
                from Admin → Coaches and the checked checkbox is the single remove
                control on every row. #790: a 1:1 link books one coach, so it gets
                one dropdown instead (OneOnOneCoachSelect). */}
            <div>
                <FieldLabel htmlFor={isRoundRobin ? undefined : 'bl-assigned-users'} className="mb-1.5 block">
                    {isRoundRobin ? 'Round Robin Coaches' : 'Assigned Coach'}
                </FieldLabel>
                {isRoundRobin ? (
                    <div id="bl-assigned-users" tabIndex={-1}
                        className="border border-stone-200 rounded-lg bg-stone-50/60 max-h-56 overflow-y-auto focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-500"
                        data-testid="bl-assigned-users" {...errorProps('bl-assigned-users', assigneesError)}>
                        {members.length === 0 && (
                            <p className="px-3 py-3 text-sm text-stone-500" data-testid="bl-assigned-users-empty">
                                {MEMBERS_STATUS_TEXT[membersStatus]}
                            </p>
                        )}
                        {members.map(row => {
                            const assigned = form.assigned_users.find(a => a.user_id === row.user_id);
                            const flag = rowFlag(row);
                            const canAdd = row.addable;
                            const hintId = flag ? `bl-member-hint-${row.user_id}` : undefined;
                            return (
                                <div key={row.user_id} data-testid={`bl-member-${row.user_id}`}
                                    className="flex items-center gap-3 px-3 py-2.5 hover:bg-white border-b border-stone-100 last:border-0">
                                    <Checkbox checked={!!assigned} disabled={!assigned && !canAdd}
                                        onCheckedChange={() => toggleUser(row.user_id)}
                                        aria-label={`Assign ${row.display_name}`} aria-describedby={hintId} />
                                    {row.photo_url
                                        ? <img src={row.photo_url} alt="" className="w-7 h-7 rounded-full object-cover shrink-0" />
                                        : <span aria-hidden="true" className="w-7 h-7 rounded-full bg-stone-200 text-stone-600 text-[11px] font-semibold flex items-center justify-center shrink-0">{initials(row.display_name)}</span>}
                                    <div className="flex-1 min-w-0">
                                        <p className={`text-sm truncate ${canAdd || assigned ? 'text-stone-800' : 'text-stone-400'}`}>{row.display_name}</p>
                                        {row.title && <p className="text-xs text-stone-500 truncate">{row.title}</p>}
                                        {flag && <p id={hintId} className="text-xs text-amber-700 truncate">{flag.hint}</p>}
                                    </div>
                                    {flag && <span className="text-xs text-amber-800 bg-amber-50 px-1.5 py-0.5 rounded shrink-0">{flag.badge}</span>}
                                    {row.calendar_connected && <span className="text-xs text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded shrink-0" title="Calendar connected">Cal</span>}
                                    {assigned && (
                                        <div className="flex items-center gap-1 shrink-0">
                                            <span className="text-xs text-stone-400">Turns:</span>
                                            <Input type="number" min="0" max="10" value={assigned.weight}
                                                onChange={e => updateWeight(row.user_id, e.target.value)}
                                                aria-label={`Turns for ${row.display_name}`}
                                                className="w-14 h-7 text-xs text-center" data-testid={`weight-${row.user_id}`} />
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <OneOnOneCoachSelect value={form.assigned_users[0]?.user_id || ''} members={members}
                        membersStatus={membersStatus} error={assigneesError} onPick={onPickCoach} />
                )}
                <FieldError id="bl-assigned-users" error={assigneesError} />
                {isRoundRobin && (
                    <FieldHint>Coaches take bookings in the order set in Admin → Coaches. Turns = how many bookings in a row a coach takes when it's their turn; 0 = skip. Busy times are read from Google and Outlook calendars and existing bookings.</FieldHint>
                )}
            </div>

            {/* #758: the guest may pick a coach on the public page. Round robin
                only — the server ignores the flag on 1:1 links. */}
            {isRoundRobin && (
                <div className="flex items-start justify-between gap-4" data-testid="bl-guest-choice-row">
                    <div className="min-w-0">
                        <FieldLabel htmlFor="bl-guest-choice">Let guests choose a coach</FieldLabel>
                        <FieldHint id="bl-guest-choice-hint">Guests can pick a coach or keep "Any available coach". Times always reflect who is free.</FieldHint>
                    </div>
                    <Switch id="bl-guest-choice" data-testid="bl-guest-choice"
                        checked={form.allow_guest_coach_choice === true}
                        onCheckedChange={(v) => setForm({ ...form, allow_guest_coach_choice: v === true })}
                        aria-describedby="bl-guest-choice-hint" className="mt-1 shrink-0" />
                </div>
            )}
        </div>
    );
}
